// Controlled tag taxonomy for the Catalog Manager editor.
//
// Mirrors `b2b-infrastructure/api/common/food_ingest/tags.py`. The editor only
// lets users pick tags from this list (the pipeline drops anything else), so
// keep these in sync by hand.
//
// TODO(tag-catalog): share a single source of truth with the Python taxonomy
// (e.g. generate this file) so the two can't drift.

/** Food-group + NOVA processing-level categories. Normally exactly one applies. */
export const CATEGORY_TAGS: string[] = [
  "NOVA1",
  "NOVA2",
  "NOVA3",
  "NOVA4",
  "alcoholic beverages",
  "baby foods",
  "baked goods",
  "beverages",
  "breakfast cereals",
  "candies and sweets",
  "cereal grains and pasta",
  "cooking and baking ingredients",
  "dairies and products",
  "dairy substitutes",
  "desserts",
  "eggs and products",
  "fats and oils",
  "food additives",
  "fried foods",
  "fruits and products",
  "legumes and products",
  "meals",
  "meat and products",
  "nuts and seeds products",
  "pickles",
  "sauces, dips, spreads, gravies",
  "seafood and products",
  "seaweeds and products",
  "snack and protein bars",
  "snacks",
  "soups",
  "spices and condiments",
  "stuffing",
  "supplement",
  "sweeteners",
  "unbranded supplement",
  "vegetables and products",
];

/** Dietary pattern / lifestyle flags. */
export const DIET_PATTERN_TAGS: string[] = [
  "vegan",
  "vegetarian",
  "pescatarian",
  "paleo",
  "halal",
  "kosher",
];

/** Allergen / ingredient-presence warnings (US Big 9 + EU 14 + coconut & corn). */
export const ALLERGEN_TAGS: string[] = [
  "contains_dairy",
  "contains_egg",
  "contains_fish",
  "contains_shellfish",
  "contains_crustaceans",
  "contains_mollusks",
  "contains_nuts",
  "contains_peanuts",
  "contains_coconut",
  "contains_soy",
  "contains_sesame",
  "contains_wheat",
  "contains_gluten",
  "contains_corn",
  "contains_mustard",
  "contains_celery",
  "contains_lupin",
  "contains_sulfites",
  "contains_gelatin",
  "contains_msg",
  "contains_pork",
  "contains_alcohol",
  "contains_caffeine",
];

/** Positive dietary claims / "free-from" flags. */
export const CLAIM_TAGS: string[] = [
  "gluten free",
  "dairy free",
  "lactose free",
  "egg free",
  "soy free",
  "nut free",
  "peanut free",
  "wheat free",
  "grain free",
  "corn free",
  "cholesterol free",
  "sugar free",
  "no added sugar",
  "sodium free",
  "low sodium",
  "fat free",
  "low fat",
  "low calorie",
  "low carb",
  "high protein",
  "high fiber",
  "whole grain",
  "plant based",
  "no artificial sweeteners",
  "no artificial flavors",
  "no artificial colors",
  "no preservatives",
  "organic",
  "non gmo",
];

/**
 * Allergen / dietary / derived flags. Several can apply. Kept as a single flat
 * list (pattern + allergen + claim) for anywhere that needs "all dietary tags".
 */
export const DIETARY_TAGS: string[] = [
  ...DIET_PATTERN_TAGS,
  ...ALLERGEN_TAGS,
  ...CLAIM_TAGS,
];

/**
 * Keto + UPF score tags. These are derived automatically by the pipeline from
 * the nutrients and NOVA level — surfaced here so the editor can display them
 * and keep them in the allowed set, but they normally shouldn't be hand-set.
 */
export const KETO_TAGS: string[] = [
  "keto_friendly",
  "keto_moderate",
  "keto_not",
];
export const UPF_TAGS: string[] = [
  "upf_none",
  "upf_low",
  "upf_moderate",
  "upf_high",
];
export const SCORE_TAGS: string[] = [...KETO_TAGS, ...UPF_TAGS];

export const ALL_TAGS: string[] = [
  ...CATEGORY_TAGS,
  ...DIETARY_TAGS,
  ...SCORE_TAGS,
];

const _BY_LOWER: Record<string, string> = {};
ALL_TAGS.forEach((t) => {
  _BY_LOWER[t.toLowerCase()] = t;
});

/** Resolve a tag to its canonical casing if it's in the taxonomy, else null. */
export const canonicalTag = (tag: string): string | null =>
  _BY_LOWER[tag.trim().toLowerCase()] ?? null;

/** Human-friendly grouping for the tag picker. */
export const TAG_GROUPS: { label: string; tags: string[] }[] = [
  { label: "Category / NOVA", tags: CATEGORY_TAGS },
  { label: "Diet & lifestyle", tags: DIET_PATTERN_TAGS },
  { label: "Allergens (contains)", tags: ALLERGEN_TAGS },
  { label: "Claims (free-from)", tags: CLAIM_TAGS },
  { label: "Keto / UPF score (auto)", tags: SCORE_TAGS },
];
